import * as React from 'react'
import { Helmet } from 'react-helmet'
import { Link, browserHistory } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Button, Menu, Icon, Tooltip, Alert } from 'antd'
import ReactGA from 'react-ga4'
import moment from 'moment'
import { PermissionNames } from '@getgreenline/homi-shared'
import Div100vh from 'react-div-100vh'
import { CurrentUserStore } from '../../stores/CurrentUserStore'
import { NoPermissionsResult } from '../../components/NoPermissionsResult'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { FormError } from '../../components/FormError'
import { Routes } from '../../constants/Routes'
import { LoadingAnimation } from '../../components/LoadingAnimation'
import { AnnounceKitBadge } from '../../components/AnnounceKitBadge'
import Logo from '../../images/logos/greenline-new-logo.svg'
import BlazeLogo from '../../images/logos/blaze-branding-logo.svg'

import 'react-virtualized/styles.css'
import './index.scss'
import ImgBannerCanadaDay from '../../images/banner-canada-day.png'
import { Track } from '../../Track'
import { ServerStatus } from './ServerStatus'
import { LocalStorage } from '../../utilities/LocalStorage'
import { MenuTag } from '../../components/BetaTag'
import { getCognitoUser } from '../../utilities/auth'
import { BLAZE_COMPANY } from '../../constants/general'
import { parseErrorMsg } from '../../utilities/helpers'
import { THEME_COLORS } from '@getgreenline/shared'
import { Dropdown } from 'antd-v4'

// Milliseconds before forcing the next menu click to be a reload
const REFRESH_TIMER = 900000 // 15 minutes

interface Props {
  currentUserStore?: CurrentUserStore
  currentCompanyStore?: CurrentCompanyStore
  params?: {
    companyId: number
  }
}

interface State {
  errorMessage?: string
  openMenuKeys: string[]
  reloadNextClick: boolean
}

// Keys of expandable menu items
const rootSubmenuKeys = [
  'settings',
  'integrations',
  'products',
  'inventory',
  'customers',
  'employees',
  'sales',
  'reports',
]

function getUrlKeys() {
  // Represents the parent + second level menu item
  // Any further nested entries are ignored
  const postDashboardUrl = window.location.pathname.split('/dashboard/')[1] // inventory/audits/SADF123
  const split = postDashboardUrl.split('/')
  const uptoFirst2UrlComponents = split.slice(0, 2)
  const currentUrlKey = uptoFirst2UrlComponents.join('/') // inventory/audits

  // Represents the parent menu item
  const rootUrlKey = split[0]

  return {
    urlKey: currentUrlKey,
    rootKey: rootUrlKey,
  }
}

@inject('currentUserStore', 'currentCompanyStore')
@observer
export class Dashboard extends React.Component<React.PropsWithChildren<Props>, State> {
  constructor(props: React.PropsWithChildren<Props>) {
    super(props)

    this.state = {
      openMenuKeys: [getUrlKeys().rootKey],
      reloadNextClick: false,
    }
  }

  async componentDidMount() {
    const { currentCompanyStore, currentUserStore } = this.props
    const company = currentCompanyStore!.company!
    const user = currentUserStore!.currentUser!
    const userCompanies = currentUserStore!.userCompanies || []

    currentCompanyStore?.getViewEcommerceSettings()
    currentCompanyStore?.getCanViewDigitalSignage()
    currentCompanyStore?.getCanUseGlobalLogin()

    if (user.userType === 'device') {
      browserHistory.push(Routes.POS_INDEX)
    }

    Track.identify(user.id, {
      id: user.id,
      email: user.email,
      name: user.name,
      createDate: user.createDate,
    })

    // Only track company if not an admin (since admins can log into multiple companies)
    if (!user.isAdmin) {
      Track.setGroup(company.id, {
        id: company.id,
        name: company.name,
        province: company.province,
        createDate: company.createDate,
        isPaid: company.isPaid,
        isLicensed: company.isLicensed,
      })
    }

    // Load user permissions
    try {
      if (!currentUserStore!.userPermissions) {
        currentUserStore!.getMyPermissions()
      }
    } catch (error) {
      this.setState({
        errorMessage:
          parseErrorMsg(error) ||
          `There was an error getting your permissions. Please contact ${BLAZE_COMPANY} support.`,
      })
    }

    try {
      if (userCompanies.length === 0) {
        currentUserStore!.getMyCompanies()
      }
    } catch (error) {
      this.setState({
        errorMessage:
          parseErrorMsg(error) ||
          `There was an error getting your companies. Please contact ${BLAZE_COMPANY} support.`,
      })
    }
    await currentCompanyStore!.getWeedmapsIntegrationStatus()
    // Set the timer
    setTimeout(() => this.setState({ reloadNextClick: true }), REFRESH_TIMER)
  }

  onOpenChange(change: string[]) {
    const { openMenuKeys } = this.state
    const latestOpenKey = change.find((key) => openMenuKeys.indexOf(key) === -1)!
    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      this.setState({ openMenuKeys: change })
    } else {
      this.setState({ openMenuKeys: latestOpenKey ? [latestOpenKey] : [] })
    }
  }

  onLinkClick = (e: any) => {
    e.stopPropagation()
    this.props.currentUserStore!.getMyPermissions()
  }
  /**
   * controlls if the user is already seeing a report and clicks in Insights again, it'll only display the secondary navigation without redirecting to .../dashboard/reports/insights
   * @param e click event
   */
  handleInsightSectionClick = (e: any) => {
    const pattern = /.+\/dashboard\/reports\/insights\/.*$/
    const shouldNavigate = !pattern.test(window.location.pathname)
    if (!shouldNavigate) {
      e.preventDefault()
    }
    this.props.currentCompanyStore?.setDisplayInsightsReportNavBar(true)
    this.onLinkClick(e)
  }

  // Get react-helmet to play well with Google Analytics
  // Reference: https://github.com/nfl/react-helmet/issues/189#issuecomment-520231585
  onHelmetChange(change: { metaTags: any[] }) {
    const { metaTags } = change
    if (metaTags.filter((m) => m.name === 'page-loaded' && m.content === 'true').length > 0) {
      ReactGA.send({ hitType: 'pageview', page: window.location.pathname, title: document.title })
    }
  }

  getLinkComponent(url: string, children: JSX.Element | string) {
    const { reloadNextClick } = this.state
    if (reloadNextClick) {
      return (
        <a href={url} onClick={this.onLinkClick}>
          {children}
        </a>
      )
    }
    return (
      <Link to={url} onClick={this.onLinkClick}>
        {children}
      </Link>
    )
  }

  logout = async () => {
    const { currentUserStore } = this.props
    const cognitoUser = getCognitoUser()
    if (cognitoUser) {
      currentUserStore?.logoutV2()
    } else {
      currentUserStore?.logout()
    }
  }

  userCompaniesDropdown = () => (
    <Menu
      onClick={({ key: companyId }) => {
        window.location.href = `/company/${companyId}/dashboard`
      }}
    >
      {(this.props.currentUserStore?.userCompanies || []).map((company) => (
        <Menu.Item key={company.entity.id}>{company.entity.name}</Menu.Item>
      ))}
    </Menu>
  )

  render() {
    const { currentUserStore, currentCompanyStore, params } = this.props
    const company = currentCompanyStore!.company!
    const currentUser = currentUserStore!.currentUser!
    const userCompanies = currentUserStore!.userCompanies || []
    const companyDashboardUrl = `/company/${params!.companyId}/dashboard`

    const permissions = currentUserStore!.userPermissions
    const isInsightsNavbarDisplayed = currentCompanyStore?.isInsightsNavbarDisplayed
    const userCanAccessDashboard = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_USE_ADMINISTRATIVE_DASHBOARD,
      company.id,
    )
    const userCanManageIntegrations = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_MANAGE_INTEGRATIONS,
      company.id,
    )
    const userCanViewTransfers = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_TRANSFERS,
      company.id,
    )
    const userCanEditCompanyDetails = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_COMPANY_DETAILS,
      company.id,
    )
    const userCanViewSales = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_SALES,
      company.id,
    )
    const userCanManageParkedSales = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_MANAGE_PARKED_SALES,
      company.id,
    )

    const userCanViewOCSCompliance =
      currentUserStore && currentCompanyStore
        ? currentUserStore.canViewOCSCompliance(currentCompanyStore)
        : false

    const { errorMessage, openMenuKeys } = this.state
    const { children } = this.props

    const helmet = (
      <Helmet onChangeClientState={this.onHelmetChange}>
        <meta name='page-loaded' content='false' />
      </Helmet>
    )

    if (!permissions) {
      return (
        <div>
          {helmet}
          <LoadingAnimation />
          <FormError message={errorMessage} />
        </div>
      )
    }

    const firstName = currentUser!.name.split(' ')[0]

    if (!userCanAccessDashboard) {
      return (
        <NoPermissionsResult
          permissionName={PermissionNames.CAN_USE_ADMINISTRATIVE_DASHBOARD}
          extra={
            <a href={Routes.LOGIN}>
              <Button>Back to login screen</Button>
            </a>
          }
        />
      )
    }

    const keys = getUrlKeys()
    const today = moment().format('YYYY-MM-DD')
    const isCanadaDay = today === '2020-06-30' || today === '2020-07-01'

    const defaultIconStyles = { width: 14, height: 14 }

    const overrideApiUrl = LocalStorage.getOverrideAPIUrl()

    return (
      <div>
        {helmet}
        <div id='wrapper' className={`${isInsightsNavbarDisplayed ? 'hidden' : ''}`}>
          <div
            id={`sidebar-wrapper`}
            className='background-shadow'
            style={
              isCanadaDay
                ? {
                    backgroundColor: THEME_COLORS.BRAND.blazeBlackSideNav,
                    overflowY: 'scroll',
                    ...SideNavStyle,
                  }
                : { backgroundColor: THEME_COLORS.BRAND.blazeBlackSideNav, overflowY: 'scroll' }
            }
          >
            <div className='sidebar-nav pb-3 pt-3'>
              <div className='mb-3'>
                <Link to='/'>
                  <img className='ml-3' alt='' src={Logo} style={{ width: 124, height: 28 }} />
                </Link>

                <span style={{ position: 'absolute', right: 20, top: 25 }}>
                  <AnnounceKitBadge
                    userId={currentUser.id}
                    email={currentUser.email}
                    companyId={company.id}
                    companyName={company.name}
                    province={company.province}
                  />
                </span>
              </div>
              <div className='ml-3 text-white'>
                <Tooltip title={currentUser.email} placement='right'>
                  {firstName}
                </Tooltip>
              </div>
              <div className='ml-3 mb-3' style={{ color: THEME_COLORS.MONO.monoee }}>
                {this.props.currentCompanyStore?.canUseGlobalLogin && userCompanies.length > 1 ? (
                  <Dropdown overlay={this.userCompaniesDropdown} trigger={['click']}>
                    <small style={CompanyDropdownStyle}>
                      {company.name} <Icon type='down' />
                    </small>
                  </Dropdown>
                ) : (
                  <small>{company.name}</small>
                )}
              </div>

              <Menu
                theme='dark'
                mode='inline'
                selectedKeys={[keys.urlKey]}
                openKeys={[...openMenuKeys]}
                onOpenChange={(change) => this.onOpenChange(change)}
              >
                <Menu.Item key='home'>
                  {this.getLinkComponent(
                    companyDashboardUrl,
                    <>
                      <Icon type='home' style={defaultIconStyles} />
                      Home
                    </>,
                  )}
                </Menu.Item>

                {userCanEditCompanyDetails && (
                  <Menu.SubMenu
                    key='settings'
                    title={
                      <>
                        <Icon type='setting' style={defaultIconStyles} />
                        Settings
                      </>
                    }
                  >
                    <Menu.Item key='settings/general'>
                      {this.getLinkComponent(`${companyDashboardUrl}/settings/general`, 'General')}
                    </Menu.Item>
                    <Menu.Item key='settings/locations'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/settings/locations`,
                        'Locations',
                      )}
                    </Menu.Item>
                    <Menu.Item key='settings/pos'>
                      {this.getLinkComponent(`${companyDashboardUrl}/settings/pos`, 'POS')}
                    </Menu.Item>
                    <Menu.Item key='settings/receipts'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/settings/receipts`,
                        'Receipts',
                      )}
                    </Menu.Item>
                    <Menu.Item key='settings/taxes'>
                      {this.getLinkComponent(`${companyDashboardUrl}/settings/taxes`, 'Taxes')}
                    </Menu.Item>
                    <Menu.Item key='settings/inventory'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/settings/inventory`,
                        'Inventory',
                      )}
                    </Menu.Item>
                    <Menu.Item key='settings/giftCards'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/settings/giftCards`,
                        'Gift cards',
                      )}
                    </Menu.Item>
                    {this.props.currentCompanyStore?.canViewDigitalSignage && (
                      <Menu.Item key='settings/digitalSignage'>
                        <Link
                          to={`${companyDashboardUrl}/settings/digitalSignage`}
                          onClick={this.onLinkClick}
                        >
                          <div>
                            <span>Digital Signage</span>
                            <MenuTag text='Beta' color='#A5D6A7' />
                          </div>
                        </Link>
                      </Menu.Item>
                    )}
                    <Menu.Item key='settings/payments'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/settings/payments`,
                        'Payments',
                      )}
                    </Menu.Item>
                    {this.props.currentCompanyStore?.canUseLoyaltyService && (
                      <Menu.Item key='settings/loyalty'>
                        {this.getLinkComponent(
                          `${companyDashboardUrl}/settings/loyalty`,
                          'Loyalty',
                        )}
                      </Menu.Item>
                    )}
                  </Menu.SubMenu>
                )}

                {userCanManageIntegrations && (
                  <Menu.SubMenu
                    key='integrations'
                    title={
                      <>
                        <Icon type='appstore' style={defaultIconStyles} />
                        Integrations
                      </>
                    }
                  >
                    <Menu.Item key='integrations'>
                      {this.getLinkComponent(`${companyDashboardUrl}/integrations`, 'Overview')}
                    </Menu.Item>
                    {currentCompanyStore!.isWeedmapsEnabled && (
                      <Menu.Item key='integrations/weedmaps'>
                        {this.getLinkComponent(
                          `${companyDashboardUrl}/integrations/weedmaps`,
                          'Weedmaps',
                        )}
                      </Menu.Item>
                    )}

                    {company.permissions.canAccessWordPress && (
                      <Menu.Item key='integrations/wooCommerce'>
                        {this.getLinkComponent(
                          `${companyDashboardUrl}/integrations/woocommerce`,
                          'WooCommerce',
                        )}
                      </Menu.Item>
                    )}

                    {company.permissions.canAccessQuickBooks && (
                      <Menu.Item key='integrations/quickBooks'>
                        {this.getLinkComponent(
                          `${companyDashboardUrl}/integrations/quickBooks/main`,
                          'QuickBooks',
                        )}
                      </Menu.Item>
                    )}
                  </Menu.SubMenu>
                )}

                <Menu.SubMenu
                  key='products'
                  title={
                    <>
                      <Icon type='tags' style={defaultIconStyles} />
                      Products
                    </>
                  }
                >
                  <Menu.Item key='products/productListing'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/products/productListing`,
                      'Product list',
                    )}
                  </Menu.Item>
                  <Menu.Item key='products/categories'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/products/categories`,
                      'Categories',
                    )}
                  </Menu.Item>
                  {company.permissions.canAccessPricingGroups && (
                    <Menu.Item key='products/pricingGroups'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/products/pricingGroups`,
                        'Pricing groups',
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item key='products/suppliers'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/products/suppliers`,
                      'Brands & Vendors',
                    )}
                  </Menu.Item>
                  <Menu.Item key='products/discounts'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/products/discounts`,
                      'Discounts',
                    )}
                  </Menu.Item>
                  <Menu.Item key='products/sample-direct'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/products/sample-direct`,
                      'Sample Direct',
                    )}
                  </Menu.Item>
                </Menu.SubMenu>

                <Menu.SubMenu
                  key='inventory'
                  title={
                    <>
                      <Icon type='barcode' style={defaultIconStyles} />
                      Inventory
                    </>
                  }
                >
                  <Menu.Item key='inventory/overview'>
                    {this.getLinkComponent(`${companyDashboardUrl}/inventory/overview`, 'Overview')}
                  </Menu.Item>
                  <Menu.Item key='inventory/audit'>
                    {this.getLinkComponent(`${companyDashboardUrl}/inventory/audit`, 'Audits')}
                  </Menu.Item>
                  {userCanViewTransfers && (
                    <Menu.Item key='inventory/transfer'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/inventory/transfer`,
                        'Transfers',
                      )}
                    </Menu.Item>
                  )}
                  {company.permissions.canAccessInventoryConversions && (
                    <Menu.Item key='inventory/convert'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/inventory/convert`,
                        'Conversions',
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item key='inventory/alerts'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/inventory/alerts`,
                      'Low stock alerts',
                    )}
                  </Menu.Item>
                </Menu.SubMenu>

                {currentUserStore!.hasPermissionAtEntity(
                  PermissionNames.CAN_VIEW_CUSTOMERS,
                  company.id,
                ) && (
                  <Menu.SubMenu
                    key='customers'
                    title={
                      <>
                        <Icon type='idcard' style={defaultIconStyles} />
                        Customers
                      </>
                    }
                  >
                    <Menu.Item key='customers/list'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/customers/list`,
                        <div>
                          <span>Customer list</span>
                        </div>,
                      )}
                    </Menu.Item>
                    {company.permissions.canAccessLoyalty && (
                      <Menu.Item key='customers/loyalty'>
                        {this.getLinkComponent(
                          `${companyDashboardUrl}/customers/loyalty`,
                          'Loyalty',
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item key='customers/outreach'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/customers/outreach`,
                        <div>
                          <span>Outreach</span>
                        </div>,
                      )}
                    </Menu.Item>
                  </Menu.SubMenu>
                )}

                {currentUserStore!.hasPermissionAtEntity(
                  PermissionNames.CAN_MANAGE_EMPLOYEES,
                  company.id,
                ) && (
                  <Menu.SubMenu
                    key='employees'
                    title={
                      <>
                        <Icon type='user' style={defaultIconStyles} />
                        Employees
                      </>
                    }
                  >
                    <Menu.Item key='employees/employees'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/employees/employees`,
                        'Employee list',
                      )}
                    </Menu.Item>
                    <Menu.Item key='employees/securityRoles'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/employees/securityRoles`,
                        'Security roles',
                      )}
                    </Menu.Item>
                  </Menu.SubMenu>
                )}

                {(userCanViewSales || userCanManageParkedSales) && (
                  <Menu.SubMenu
                    key='sales'
                    title={
                      <>
                        <Icon type='dollar' style={defaultIconStyles} />
                        Sales
                      </>
                    }
                  >
                    {userCanViewSales && (
                      <Menu.Item key='sales/list'>
                        {this.getLinkComponent(`${companyDashboardUrl}/sales/list`, 'Sales list')}
                      </Menu.Item>
                    )}
                    {userCanManageParkedSales &&
                      (currentCompanyStore?.canDisplayOnlineOrdersReceipts ? (
                        <Menu.Item key={'sales/orderHub'}>
                          {this.getLinkComponent(
                            `${companyDashboardUrl}/sales/orderHub`,
                            'Order hub',
                          )}
                        </Menu.Item>
                      ) : (
                        <Menu.Item key={'sales/parkedSales'}>
                          {this.getLinkComponent(
                            `${companyDashboardUrl}/sales/parkedSales`,
                            'Parked sales',
                          )}
                        </Menu.Item>
                      ))}
                  </Menu.SubMenu>
                )}

                {currentUserStore!.hasPermissionAtEntity(
                  PermissionNames.CAN_VIEW_PURCHASE_ORDERS,
                  company.id,
                ) && (
                  <Menu.Item key='purchaseOrders'>
                    {this.getLinkComponent(
                      `${companyDashboardUrl}/purchaseOrders`,
                      <>
                        <Icon type='container' style={defaultIconStyles} />
                        Purchase orders
                      </>,
                    )}
                  </Menu.Item>
                )}

                {currentUserStore!.hasPermissionAtEntity(
                  PermissionNames.CAN_VIEW_REPORTS,
                  company.id,
                ) && (
                  <Menu.SubMenu
                    key='reports'
                    title={
                      <>
                        <Icon type='line-chart' style={defaultIconStyles} />
                        Reports
                      </>
                    }
                  >
                    <Menu.Item key='reports/analytics'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/reports/analytics`,
                        'Analytics',
                      )}
                    </Menu.Item>

                    {currentCompanyStore?.canUseInsightsQuicksight && (
                      <Menu.Item key='reports/insights'>
                        <Link
                          to={`${companyDashboardUrl}/reports/insights`}
                          onClick={this.handleInsightSectionClick}
                        >
                          <div>
                            <span>Insights</span>
                            <MenuTag text='Beta' color='#A5D6A7' />
                          </div>
                        </Link>
                      </Menu.Item>
                    )}
                    {currentUserStore!.hasPermissionAtEntity(
                      PermissionNames.CAN_VIEW_BROADCASTS,
                      company.id,
                    ) &&
                      currentCompanyStore?.canUseBroadcasts && (
                        <Menu.Item key='reports/broadcasts'>
                          {this.getLinkComponent(
                            `${companyDashboardUrl}/reports/broadcasts/list`,
                            <div>
                              <span>Broadcasts</span>
                              <MenuTag text='Beta' color='#A5D6A7' />
                            </div>,
                          )}
                        </Menu.Item>
                      )}
                    <Menu.Item key='reports/shifts'>
                      {this.getLinkComponent(`${companyDashboardUrl}/reports/shifts`, 'POS shifts')}
                    </Menu.Item>
                    <Menu.Item key='reports/inventoryLogs'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/reports/inventoryLogs`,
                        'Inventory logs',
                      )}
                    </Menu.Item>
                    <Menu.Item key='reports/compliance'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/reports/compliance`,
                        'Compliance',
                      )}
                    </Menu.Item>
                  </Menu.SubMenu>
                )}
                {userCanViewOCSCompliance && (
                  <Menu.SubMenu
                    key='compliance'
                    title={
                      <>
                        <Icon type='file' style={defaultIconStyles} />
                        Compliance
                      </>
                    }
                  >
                    <Menu.Item key='compliance/settings'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/compliance/settings`,
                        'Settings',
                      )}
                    </Menu.Item>
                    <Menu.Item key='compliance/issues'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/compliance/issues`,
                        'Compliance Issues',
                      )}
                    </Menu.Item>
                    <Menu.Item key='compliance/data-reconciliation'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/compliance/data-reconciliation`,
                        'Data Reconciliation',
                      )}
                    </Menu.Item>
                    <Menu.Item key='compliance/inventory-corrections'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/compliance/inventory-corrections`,
                        'Inventory Corrections',
                      )}
                    </Menu.Item>
                    <Menu.Item key='compliance/process-on-date-logs'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/compliance/process-on-date-logs`,
                        'Process on Date Logs',
                      )}
                    </Menu.Item>
                  </Menu.SubMenu>
                )}
                {this.props.currentCompanyStore?.canViewEcommerceSettings && (
                  <Menu.SubMenu
                    key='ecommerce'
                    title={
                      <>
                        <Icon type='shopping-cart' style={defaultIconStyles} />
                        <span>BLAZE ECOM</span>
                        <MenuTag text='New' color='#A5D6A7' />
                      </>
                    }
                  >
                    <Menu.Item key='ecommerce/settings'>
                      {this.getLinkComponent(
                        `${companyDashboardUrl}/ecommerce/settings`,
                        'Settings',
                      )}
                    </Menu.Item>
                    <Menu.Item key='ecommerce/login'>
                      <a
                        target='_blank'
                        href='https://ecom.blaze.me/login'
                        onClick={this.onLinkClick}
                        rel='noreferrer'
                      >
                        Login Page
                      </a>
                    </Menu.Item>
                  </Menu.SubMenu>
                )}
              </Menu>

              <br />

              <div>
                <Button className='ml-4' onClick={this.logout}>
                  <Icon type='logout' style={defaultIconStyles} />
                  Logout
                </Button>
              </div>

              <br />

              <ServerStatus />

              <div>
                <Link to={{ pathname: 'https://blaze.me' }} target='_blank'>
                  <img
                    className='ml-4 mt-4'
                    alt=''
                    src={BlazeLogo}
                    style={{ width: 75, height: 14 }}
                  />
                </Link>
              </div>
              {overrideApiUrl && (
                <Link to='/admin/v2/development'>
                  <Alert message={`API URL: ${overrideApiUrl}`} type='warning' className='m-1' />
                </Link>
              )}
            </div>

            {isCanadaDay && (
              <>
                <div style={GatStyle} />
                <img style={BannerStyle} src={ImgBannerCanadaDay} alt='Canada day' />
              </>
            )}
          </div>

          <Div100vh id='page-content-wrapper'>
            <div className='page-content-wrapper__inner'>{children}</div>
          </Div100vh>
        </div>
      </div>
    )
  }
}

const SideNavStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column' }
const GatStyle: React.CSSProperties = { flex: 1, minHeight: '8px' }
const BannerStyle: React.CSSProperties = {
  position: 'relative',
  flex: 'none',
  margin: '0 auto 16px auto',
  width: '90%',
  height: 'auto',
}

const CompanyDropdownStyle: React.CSSProperties = {
  width: '90%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}
